import cx from 'classnames'
import dynamic from 'next/dynamic'

import { type SanityHeroModule } from '@data/sanity/queries/types/modules'

import Photo from '@components/photo'
import ComplexPortableText from '@components/complex-portable-text'

const MuxVideo = dynamic(() => import('@components/video/mux-video'))

type HeroProps = Pick<
  SanityHeroModule,
  'content' | 'contentPosition' | 'bgType' | 'photos' | 'muxVideo'
> & {
  overlay?: boolean
}

const getRatio = (ratio?: number, defaultRatio?: number) => {
  if (typeof ratio === 'undefined' || ratio === 0) {
    return defaultRatio ?? 1
  }

  return ratio
}

const Hero = ({
  content,
  contentPosition,
  bgType,
  photos,
  muxVideo,
  overlay,
}: HeroProps) => {
  const isBottomLeft = contentPosition === 'bottom-left'
  const isCenter = contentPosition === 'center'

  return (
    <section
      className={cx(
        'relative overflow-hidden bg-pageBG max-h-screen min-h-[500px] xs:min-h-[auto]',
        {
          'before:absolute before:inset-0 before:z-[1] before:bg-pageBG before:bg-opacity-80':
            overlay,
        }
      )}
    >
      <div
        className={cx('container absolute inset-0 z-10 flex flex-col', {
          'justify-end': isBottomLeft,
          'justify-center items-center': isCenter,
        })}
      >
        {content && (
          <div
            className={cx('z-10 w-full text-pageBG mt-14', {
              'md:max-w-[70%] xl:max-w-[50%] mb-4 md:mb-10': isBottomLeft,
              'lg:max-w-[55%]': isCenter,
            })}
          >
            <div
              className={cx('rc rc-hero', {
                'text-center': isCenter,
              })}
            >
              <ComplexPortableText content={content} />
            </div>
          </div>
        )}
      </div>

      <div>
        {bgType === 'mux-video' && muxVideo && (
          <MuxVideo
            video={muxVideo}
            showControls={false}
            autoplay
            muted
            loop
            customAspectRatio={{
              custom: true,
              desktop: '16:9',
              mobile: '5:7',
            }}
          />
        )}

        {bgType === 'photo' && photos && (
          <>
            {photos.desktopPhoto && (
              <Photo
                image={photos.desktopPhoto}
                fill
                priority
                className="relative w-full z-0 h-full hidden sm:block"
                style={{
                  paddingTop: `${
                    100 / getRatio(photos.desktopPhoto.customRatio, 16 / 9)
                  }%`,
                }}
                imageClassName="object-cover"
              />
            )}
            {photos.mobilePhoto && (
              <Photo
                image={photos.mobilePhoto}
                fill
                priority
                className="relative w-full z-0 h-full sm:hidden"
                style={{
                  paddingTop: `${
                    100 / getRatio(photos.mobilePhoto.customRatio, 5 / 7)
                  }%`,
                }}
                imageClassName="object-cover"
              />
            )}
          </>
        )}
      </div>
    </section>
  )
}

export default Hero
